import { RingBuffer } from './ring-buffer';
import type { CandlePoint, OHLCTick, TimedValue } from './types';

export class OHLCAggregationCache {
    private readonly candles: RingBuffer<CandlePoint>;
    private current: CandlePoint | null = null;

    constructor(
        private readonly bucketMs: number,
        capacity: number = 2048,
    ) {
        if (bucketMs <= 0) throw new Error('bucketMs must be > 0');
        this.candles = new RingBuffer<CandlePoint>(capacity);
    }

    addTick(tick: TimedValue, volume: number = 0): CandlePoint {
        const bucketTs = Math.floor(tick.t / this.bucketMs) * this.bucketMs;

        if (!this.current || this.current.ts !== bucketTs) {
            if (this.current) this.candles.push(this.current);
            this.current = {
                ts: bucketTs,
                open: tick.v,
                high: tick.v,
                low: tick.v,
                close: tick.v,
                volume,
            };
            return this.current;
        }

        this.current.high = Math.max(this.current.high, tick.v);
        this.current.low = Math.min(this.current.low, tick.v);
        this.current.close = tick.v;
        this.current.volume += volume;
        return this.current;
    }

    addOHLC(ohlc: OHLCTick): CandlePoint {
        const bucketTs = Math.floor(ohlc.t / this.bucketMs) * this.bucketMs;
        if (this.current && this.current.ts === bucketTs) {
            this.current.high = Math.max(this.current.high, ohlc.h);
            this.current.low = Math.min(this.current.low, ohlc.l);
            this.current.close = ohlc.c;
            this.current.volume += ohlc.volume || 0;
            return this.current;
        }

        if (this.current) this.candles.push(this.current);
        this.current = {
            ts: bucketTs,
            open: ohlc.o,
            high: ohlc.h,
            low: ohlc.l,
            close: ohlc.c,
            volume: ohlc.volume || 0,
        };
        return this.current;
    }

    getCandles(includeCurrent: boolean = true): CandlePoint[] {
        const base = this.candles.toArray();
        if (includeCurrent && this.current) base.push(this.current);
        return base;
    }

    clear(): void {
        this.current = null;
        this.candles.clear();
    }
}
